import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Brand } from "../components/Brand";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

interface RefFile {
  id: string;
  name: string;
  size: number;
  url: string;
}

interface RefData {
  enquiry_id: string;
  expires_at: string | null;
  files: RefFile[];
}

function fmtSize(n: number) {
  return n > 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
}

export function ReferencePage() {
  const { token } = useParams();
  const [state, setState] = useState<{ status: "loading" | "error" | "ok"; data?: RefData; msg?: string }>({
    status: "loading",
  });

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/references/${token}`)
      .then(async (r) => {
        if (!r.ok) {
          const d = await r.json().catch(() => null);
          throw new Error(d?.detail || "This reference link is invalid or has expired.");
        }
        return r.json();
      })
      .then((data) => alive && setState({ status: "ok", data }))
      .catch((e: Error) => alive && setState({ status: "error", msg: e.message }));
    return () => {
      alive = false;
    };
  }, [token]);

  return (
    <div className="min-h-screen bg-ivory texture-weave" data-testid="reference-page">
      <header className="bg-ivory/90 backdrop-blur-md border-b border-forest/10">
        <div className="max-w-3xl mx-auto px-5 py-4 flex justify-center">
          <Brand />
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-12">
        {state.status === "loading" && (
          <div className="flex flex-col items-center gap-3 py-20 text-charcoal/60" data-testid="reference-loading">
            <Loader2 className="h-8 w-8 animate-spin text-forest" />
            <p className="text-sm">Fetching your reference uploads…</p>
          </div>
        )}
        {state.status === "error" && (
          <div className="rounded-3xl border border-burgundy/25 bg-burgundy/5 p-8 text-center" data-testid="reference-error">
            <p className="font-serif text-2xl text-forest">This link has expired or is invalid.</p>
            <p className="mt-2 text-sm text-charcoal/65">
              {state.msg} Reference links stay live for 72 hours after an enquiry is saved. If you
              still need those photos, save this enquiry again on WhatsApp.
            </p>
          </div>
        )}
        {state.status === "ok" && state.data && (
          <div data-testid="reference-content">
            <h1 className="font-serif text-3xl text-forest">
              Enquiry {state.data.enquiry_id} — reference uploads
            </h1>
            <p className="mt-2 text-sm text-charcoal/65">
              {state.data.files.length} image{state.data.files.length === 1 ? "" : "s"} attached to
              this enquiry.
              {state.data.expires_at &&
                ` This private link expires on ${new Date(state.data.expires_at).toLocaleString()}.`}
            </p>
            <p className="mt-1 text-xs text-charcoal/50">
              This page shows only the reference photos for this enquiry — no personal details.
            </p>
            {state.data.files.length === 0 ? (
              <p className="mt-8 rounded-2xl border border-forest/15 bg-ivory-soft p-6 text-sm text-charcoal/70">
                No images were attached to this enquiry.
              </p>
            ) : (
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4">
                {state.data.files.map((f) => (
                  <a
                    key={f.id}
                    href={`${API}${f.url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block rounded-2xl overflow-hidden border border-forest/10 bg-white shadow-tuft"
                    data-testid={`reference-file-${f.id}`}
                  >
                    <img
                      src={`${API}${f.url}`}
                      alt={`Reference upload: ${f.name}`}
                      className="aspect-square w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                    <span className="block px-3 py-2 text-xs text-charcoal/60 truncate">{f.name} · {fmtSize(f.size)}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
